import { SiteInfo } from "@/globals";
import { getBrandByName } from "@/js/repository/BrandRepository";
import { getProducts, ProductResponse } from "@/js/repository/ProductRepository";
import Head from "next/head";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { ProductList } from "../../products";
import { getCategoriesByProducts } from "@/js/repository/CategoryRepository";
import { getProductPath } from "@/js/utils/stringUtils";
import { AdSenseBanner } from "@/components/app/AdSenseBanner";
import { useIsMobile } from "@/lib/useIsMobile";

export async function getServerSideProps(context: any) {
  const { locale } = context;
  const { brand } = context.query;
  const siteUrl = `https://${context.req.headers.host}`;
  
  if (!brand) {
    return { notFound: true };
  }

  if(brand !== brand.toLowerCase()) {
    return {
      redirect: {
        destination: `/brand/${brand.toLowerCase()}`,
        permanent: true,
      },
    }
  }

  const brandInfo = await getBrandByName(locale, brand as string);

  if (!brandInfo) {
    return { notFound: true };
  }

  const categories = await getCategoriesByProducts(locale);
  const products = await getProducts(locale, 1, 10000, undefined, undefined, brand);
  const productsCount = products.length;
  
  return {
    props: {
      brandInfo,
      products,
      siteUrl,
      productsCount,
      categories,
    },
  };
}

export default function BrandPage(props: any) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [productList, setProductList] = useState<ProductResponse[]>(props.products);

  const { brandInfo, productsCount } = props;

  useEffect(() => {
    setProductList(props.products);
  }, [props.products]);

  return (
    <>
      <Head>
        <title>{`${brandInfo.name} | ${SiteInfo.siteName}`}</title>
        <meta name="description" content={`${brandInfo.name} - ${t("siteDescription")}`} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${props.siteUrl}/brand/${brandInfo.name.toLowerCase()}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${props.siteUrl}/brand/${brandInfo.name.toLowerCase()}`} />
        <meta property="og:title" content={`${brandInfo.name} | ${SiteInfo.siteName}`} />
        <meta property="og:description" content={`${brandInfo.name} - ${t("siteDescription")}`} />
        <meta property="og:image" content={`${props.siteUrl}/logo.png`} />
        <meta property="og:site_name" content={SiteInfo.siteName} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`${props.siteUrl}/logo.png`} />
        <meta name="twitter:title" content={`${brandInfo.name} | ${SiteInfo.siteName}`} />
        <meta name="twitter:description" content={`${brandInfo.name} - ${t("siteDescription")}`} />

        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": props.siteUrl },
              { "@type": "ListItem", "position": 2, "name": "Brands", "item": `${props.siteUrl}/brands` },
              { "@type": "ListItem", "position": 3, "name": brandInfo.name }
            ]
          }, null, 2)
        }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={
          {
            __html: JSON.stringify(
            {
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "name": brandInfo.name,
              "description": `${brandInfo.name} - ${t("siteDescription")}`,
              "url": `${props.siteUrl}/brand/${brandInfo.name.toLowerCase()}`,
              "numberOfItems": productsCount,
              "category": brandInfo.name
            }, null, 2)
          }
        }>
        </script>
        {props.products?.length > 0 && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              "numberOfItems": productsCount,
              "itemListElement": props.products.slice(0, 20).map((p: ProductResponse, i: number) => ({
                "@type": "ListItem",
                "position": i + 1,
                "url": `${props.siteUrl}${getProductPath(p.detail.product_id, p.detail.product_title)}`,
                "name": p.detail.product_title?.substring(0, 100)
              }))
            }, null, 2)
          }} />
        )}
      </Head>

      <div className="mb-8">
        <section className="mb-6 rounded-lg border border-black/10 bg-white p-5 shadow-sm sm:p-6">
          <a href="/brands" className="mb-2 inline-block text-sm font-semibold text-[#fd384f] hover:underline">
            ← {t("brands")}
          </a>
          <p className="mt-3 text-xs font-black uppercase tracking-normal text-[#fd384f]">{t("curatedByProsense")}</p>
          <h1 className="mt-2 text-2xl font-black text-[#101820] sm:text-3xl">{brandInfo.name}</h1>
          <p className="mt-2 text-sm text-gray-500">{productsCount} {t("products")}</p>
          
          {brandInfo.categories && brandInfo.categories.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {brandInfo.categories.map((category: string, index: number) => (
                <span key={index} className="rounded-md bg-[#eef7f4] px-2 py-1 text-xs font-semibold text-[#0f766e]">
                  {category}
                </span>
              ))}
            </div>
          )}
        </section>

        {/* 상품 목록 */}
        <div className="mt-8">
          <h2 className="mb-4 text-xl font-black text-[#101820]">{t("productsCapital")}</h2>
          {productsCount === 0 ? (
            <div className="rounded-lg border border-black/10 bg-white p-8 text-center text-gray-500">
              <p>{t("products")} {t("searchNoResults")}</p>
              <a href="/brands" className="inline-block mt-3 text-[#fd384f] font-medium hover:underline">{t("brands")}</a>
            </div>
          ) : (
            <>
              <div className="my-6">
                <AdSenseBanner id="brand-content-top" slot="4220411723" height={isMobile ? 100 : 120} />
              </div>
              <ProductList products={productList} />
              <div className="my-6">
                <AdSenseBanner id="brand-content-bottom" slot="4220411723" height={isMobile ? 180 : 240} />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
} 
